import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  Col,
  Empty,
  Form,
  Row,
  Space,
  Tabs,
  Collapse,
} from "antd";
import dayjs from "dayjs";
import { debounce, isEmpty, isNull } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  listReminders,
  clearSelectedReminderRows,
  setSelectedReminderRows,
  setActiveReminderTab,
  clearReminderProfiles,
  clearOperationResult,
} from "../../store/reminder/reminderActions";
import { setTablePreserveOnReload } from "../../store/ui/uiActions";
import AgencyMultiSelectEx from "../shared/form/AgencyMultiSelectEx";
import DynamicProducerSelect from "../shared/form/DynamicProducerSelect";
import {
  fixedDateRanges,
  dateRangeConfigs,
} from "../shared/form/fixedDateRanges";
import { getInitialAgency } from "../shared/form/getInitialAgency";
import AdvancedRangePicker from "../shared/form/reactHookForm/AdvancedRangePicker";
import { dateFormat, toTZNeutralFormat } from "../shared/formatting";
import { agenciesListEqual } from "../shared/sorting";
import { SearchableTable } from "../shared/table/SearchableTable";
import { tableNames } from "../shared/table/tableNames";
import {
  manageRemindersColumns,
  remindersSentColumns,
} from "./manageRemindersColumns";
import { manageRemindersSchema } from "./manageRemindersSchemas";
import SearchMultiSelectEx from "../shared/form/SearchMultiSelectEx";
import { listReminderProfiles } from "../../store/reminder/reminderActions";
import RenewalRequestStatus from "./RenewalRequestStatus";
import useTableRowSelection from "../shared/table/components/useTableRowSelection";
import openNotificationOperationResult from "../shared/openNotificationOperationResult";
import BulkUpdateModal from "./bulkUpdate/BulkUpdateModal";
import BulkUpdateActionMenu from "./bulkUpdate/BulkUpdateActionMenu";

//
const initialValues = {
  AgencyKeys: [],
  ExpiryDateFixedDateRange: fixedDateRanges.ALL_DATES,
  ExpiryDate: [null, null],
  UpdatedDateFixedDateRange: fixedDateRanges.LAST_X_DAYS,
  UpdatedDateNumberOfDays: 7,
  ShowAutoplanPolicies: true,
  ShowSpecialPolicies: true,
  PolicyStatusActive: true,
  PolicyStatusExpired: true,
  PolicyStatusCancelled: false,
  PolicyStatusRenewed: false,
  PolicyStatusLost: false,
  ProducerCode1Keys: [],
  ProducerCode2Keys: [],
};

const colSize = {
  xs: 24,
  sm: 24,
  md: 24,
  lg: 24,
  xl: 12,
  xxl: 12,
};

const firstLayout = {
  labelCol: {
    xs: null,
    sm: { span: 7 },
    md: { span: 6 },
    lg: { span: 5 },
    xl: { span: 6 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: null,
    sm: { span: 17 },
    md: { span: 18 },
    lg: { span: 19 },
    xl: { span: 18 },
    xxl: { span: 19 },
  },
};

const secondLayout = {
  labelCol: {
    xs: null,
    sm: { span: 7 },
    md: { span: 6 },
    lg: { span: 5 },
    xl: { span: 6 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: null,
    sm: { span: 17 },
    md: { span: 18 },
    lg: { span: 19 },
    xl: { span: 16 },
    xxl: { span: 17 },
  },
};

const ManageRemindersForm = ({ agencies }) => {
  // Form
  const {
    reset,
    control,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { defaultValues, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: initialValues,
    resolver: yupResolver(manageRemindersSchema),
  });

  //
  const dispatch = useDispatch();

  //
  const agencyKeys = watch("AgencyKeys");

  //
  const searchValues = useSelector((state) => state.reminder.searchValues);

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [viewDetails, setViewDetails] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null);
  const updateProducerCodes = useRef(true);

  //
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const loading = useSelector((state) => state.reminder.loading);

  const producerCodes1List = useSelector(
    (state) => state.reminder.producerCodes1List
  );
  const producerCodes2List = useSelector(
    (state) => state.reminder.producerCodes2List
  );
  const producerCodes1Loading = useSelector(
    (state) => state.reminder.producerCodes1Loading
  );
  const producerCodes2Loading = useSelector(
    (state) => state.reminder.producerCodes2Loading
  );

  const reminderProfiles = useSelector(
    (state) => state.reminder.reminderProfiles
  );

  const operationResult = useSelector(
    (state) => state.reminder.operationResult
  );

  const tableData = useSelector((state) =>
    state.ui.tables.find((b) => b.name === tableNames.ManageReminders)
  );
  const tableCurrentDataSource = tableData?.extra?.currentDataSource;
  const {
    list,
    selectedRows,
    selectedRowRecords,
    filteredSelectedRowsCount,
    doNotPreserveSelections,
    rowKeyExtractor,
    rowSelection,
  } = useTableRowSelection({
    listSelector: (state) => state.reminder.data,
    selectedRowsSelector: (state) => state.reminder.selectedRows,
    setSelectedRowsAction: setSelectedReminderRows,
    clearSelectedRowsAction: clearSelectedReminderRows,
    rowKeyExtractor: (record) => record.Id,
    filteredDataSource: tableCurrentDataSource,
  });

  const refreshData = async (values = getValues(), doNotPreserve = true) => {
    if (doNotPreserve) {
      doNotPreserveSelections();
    }

    // Explicitly trigger validation and wait for result
    const isFormValid = await trigger();

    if (dispatch && !isEmpty(values.ReminderProfileIds) && isFormValid) {
      dispatch(listReminders(values, updateProducerCodes.current));
    }
    updateProducerCodes.current = false;
  };

  const debouncedRefreshData = useMemo(() => debounce(refreshData, 1000), []);

  // Replace your local state with Redux state
  const activeTab = useSelector((state) => state.reminder.activeTab);

  // Update tab change handler
  const handleTabChange = (tabKey) => {
    // Switch tabs
    dispatch(setActiveReminderTab(tabKey));

    // Clear all selected rows
    dispatch(clearSelectedReminderRows());
  };

  const tabFilteredData = useMemo(() => {
    if (!list) return [];
    switch (activeTab) {
      case "1":
        return list.filter(
          (item) => item.ProcessingStatus === RenewalRequestStatus.Waiting
        );
      case "2":
        return list.filter(
          (item) => item.ProcessingStatus === RenewalRequestStatus.Processing
        );
      case "3":
        return list.filter(
          (item) => item.ProcessingStatus === RenewalRequestStatus.ResponseSent
        );
      case "4":
        return list.filter(
          (item) => item.ProcessingStatus === RenewalRequestStatus.Reminder
        );
      case "5":
        return list.filter(
          (item) => item.ProcessingStatus === RenewalRequestStatus.NotContacted
        );
      case "6":
        return list.filter(
          (item) => item.ProcessingStatus === RenewalRequestStatus.Rejected
        );
      case "7":
        return list.filter(
          (item) => item.ProcessingStatus === RenewalRequestStatus.Completed
        );
      case "8":
        return list;
      default:
        return [];
    }
  }, [list, activeTab]);

  //
  useEffect(() => {
    openNotificationOperationResult(operationResult, () =>
      dispatch(clearOperationResult())
    );
  }, [operationResult]);

  //
  useEffect(() => {
    return () => {
      debouncedRefreshData.cancel();
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(searchValues)) {
      // Reset form with search values
      reset(searchValues);

      // Trigger validation and refresh data
      trigger().then(() => {
        updateProducerCodes.current = true;
        debouncedRefreshData.cancel();
        debouncedRefreshData(searchValues);
      });
    }
  }, []);

  // Then, modify the agencies effect to only reset when needed
  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      // Only reset to default if we don't have search values, and it's not initial load
      if (isEmpty(searchValues)) {
        resetFieldsToDefault();
      }
    }
  }, [agencies]);

  //
  const resetFieldsToDefault = () => {
    var initialAgency = getInitialAgency(homeAgency, agencies);

    // Use search values if available, otherwise use defaults
    var newValues = {
      // ...(isEmpty(searchValues) ? initialValues : searchValues),
      ...initialValues,
      AgencyKeys: [initialAgency],
      ExpiryDateFixedDateRange: fixedDateRanges.ALL_DATES,
      UpdatedDateFixedDateRange: fixedDateRanges.LAST_X_DAYS,
      UpdatedDateNumberOfDays: 7,
      UpdatedDate: [
        toTZNeutralFormat(dayjs().add(-7, "day").startOf("day")),
        toTZNeutralFormat(dayjs().endOf("day")),
      ],
    };

    reset(newValues);
    trigger();
    debouncedRefreshData.cancel();
    refreshData(newValues);
  };

  const viewDetailsOnCancelHandler = (shouldReload) => {
    setViewDetails(false);
    setBulkActionType(null);
    if (shouldReload === true) {
      dispatch(setTablePreserveOnReload(tableNames.ManageReminders, true));
      updateProducerCodes.current = true;
      debouncedRefreshData.cancel();
      refreshData(getValues());
    }
  };

  useEffect(() => {
    if (!isEmpty(agencyKeys) && !isNull(agencyKeys)) {
      dispatch(listReminderProfiles(agencyKeys));
    }
  }, [agencyKeys]);

  useEffect(() => {
    setValue(
      "ReminderProfileIds",
      reminderProfiles.map((profile) => profile.value)
    );
    updateProducerCodes.current = true;
    debouncedRefreshData();
  }, [reminderProfiles, setValue]);

  const maskSensitiveData = useSelector(
    (state) => state.actor?.details?.data?.MaskSensitiveData
  );

  const getColumnsForTab = (tabKey) => {
    switch (tabKey) {
      case "4": // Reminders Sent
        return remindersSentColumns(maskSensitiveData);
      // Add more cases for other tabs as needed
      default:
        return manageRemindersColumns(maskSensitiveData);
    }
  };

  const columns = useMemo(
    () => getColumnsForTab(activeTab),
    [activeTab, maskSensitiveData]
  );

  return (
    <div>
      <Row>
        <Col span={24}>
          <Collapse
            defaultActiveKey={["1"]}
            items={[
              {
                key: "1",
                label: "Parameters",
                children: (
                  <Form
                    size="small"
                    autoComplete="off"
                    onFinish={() => {}}
                    style={{ marginTop: "16px" }}
                  >
                    <Row>
                      <Col {...colSize}>
                        <AgencyMultiSelectEx
                          layout={firstLayout}
                          formLabel="Agency"
                          required={true}
                          agencies={currentAgencies}
                          isLoading={isEmpty(defaultValues.AgencyKeys)}
                          setValue={setValue}
                          control={control}
                          name="AgencyKeys"
                          onChange={(newAgencyKeys) => {
                            if (
                              newAgencyKeys != null &&
                              newAgencyKeys.length > 0
                            ) {
                              updateProducerCodes.current = true;
                              debouncedRefreshData();
                            } else {
                              dispatch(clearReminderProfiles());
                              setValue("ProducerCode1Keys", [], {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                              setValue("ProducerCode2Keys", [], {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                              setValue("ReminderProfileIds", [], {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      </Col>
                      <Col {...colSize}>
                        <AdvancedRangePicker
                          formItem={{
                            layout: firstLayout,
                            label: "Expiry Date",
                          }}
                          useDynamicName={true}
                          control={control}
                          setValue={setValue}
                          allowClear={true}
                          dateRangeConfig={
                            dateRangeConfigs.NON_TRANSACTION_WITH_ALL
                          }
                          name="ExpiryDate"
                          format={dateFormat}
                          afterChange={() => {
                            updateProducerCodes.current = true;
                            debouncedRefreshData();
                          }}
                        />
                      </Col>
                      <Col {...colSize}>
                        <SearchMultiSelectEx
                          layout={firstLayout}
                          required={true}
                          formLabel="Reminder Profile"
                          control={control}
                          name="ReminderProfileIds"
                          data={reminderProfiles}
                          setValue={setValue}
                          onChange={(newProfileIds) => {
                            if (
                              newProfileIds != null &&
                              newProfileIds.length > 0
                            ) {
                              updateProducerCodes.current = true;
                              debouncedRefreshData();
                            }
                          }}
                        />
                      </Col>
                      <Col {...colSize}>
                        <AdvancedRangePicker
                          formItem={{
                            layout: firstLayout,
                            label: "Updated Date",
                          }}
                          useDynamicName={true}
                          dateRangeConfig={
                            dateRangeConfigs.NON_TRANSACTION_NO_FUTURE_WITH_ALL
                          }
                          control={control}
                          setValue={setValue}
                          allowClear={true}
                          name="UpdatedDate"
                          format={dateFormat}
                          afterChange={() => {
                            updateProducerCodes.current = true;
                            debouncedRefreshData();
                          }}
                        />
                      </Col>

                      <Col {...colSize}>
                        <Form.Item
                          {...firstLayout}
                          label="Policy Type"
                          name="PolicyType"
                          required
                          valuePropName="checked"
                        >
                          <Space>
                            <Controller
                              control={control}
                              name="ShowAutoplanPolicies"
                              render={({ field }) => (
                                <Checkbox
                                  checked={field.value}
                                  onChange={(e) => {
                                    const currentValues = getValues();
                                    const autoplanPolicies = e.target.checked;
                                    const specialPolicies =
                                      !currentValues.ShowSpecialPolicies &&
                                      !e.target.checked
                                        ? true
                                        : currentValues.ShowSpecialPolicies;

                                    setValue(
                                      "ShowAutoplanPolicies",
                                      autoplanPolicies,
                                      {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                      }
                                    );

                                    if (
                                      currentValues.ShowSpecialPolicies !=
                                      specialPolicies
                                    ) {
                                      setValue(
                                        "ShowSpecialPolicies",
                                        specialPolicies,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        }
                                      );
                                    }

                                    debouncedRefreshData();
                                  }}
                                  inputRef={field.ref}
                                  name="ShowAutoplanPolicies"
                                >
                                  Autoplan Policies
                                </Checkbox>
                              )}
                            />
                            <Controller
                              control={control}
                              name="ShowSpecialPolicies"
                              render={({ field }) => (
                                <Checkbox
                                  checked={field.value}
                                  name="ShowSpecialPolicies"
                                  onChange={(e) => {
                                    const currentValues = getValues();
                                    const specialPolicies = e.target.checked;
                                    const autoplanPolicies =
                                      !currentValues.ShowAutoplanPolicies &&
                                      !e.target.checked
                                        ? true
                                        : currentValues.ShowAutoplanPolicies;

                                    setValue(
                                      "ShowSpecialPolicies",
                                      specialPolicies,
                                      {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                      }
                                    );

                                    if (
                                      currentValues.ShowAutoplanPolicies !=
                                      autoplanPolicies
                                    ) {
                                      setValue(
                                        "ShowAutoplanPolicies",
                                        autoplanPolicies,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        }
                                      );
                                    }

                                    debouncedRefreshData();
                                  }}
                                  inputRef={field.ref}
                                >
                                  Special Coverage Policies
                                </Checkbox>
                              )}
                            />
                          </Space>
                        </Form.Item>
                      </Col>
                      <Col {...colSize}>
                        <Form.Item
                          {...firstLayout}
                          label="Policy Status"
                          name="PolicyStatus"
                          required
                          valuePropName="checked"
                        >
                          <Space direction="horizontal">
                            <Space>
                              <Controller
                                control={control}
                                name="PolicyStatusActive"
                                render={({ field }) => (
                                  <Checkbox
                                    checked={field.value}
                                    onChange={(e) => {
                                      const currentValues = getValues();
                                      const isChecked = e.target.checked;
                                      const isAllPoliciesUnchecked =
                                        !isChecked &&
                                        !currentValues.PolicyStatusLost &&
                                        !currentValues.PolicyStatusCancelled &&
                                        !currentValues.PolicyStatusExpired &&
                                        !currentValues.PolicyStatusRenewed;

                                      if (!isAllPoliciesUnchecked) {
                                        setValue(
                                          "PolicyStatusActive",
                                          isChecked,
                                          {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                          }
                                        );

                                        debouncedRefreshData();
                                      }
                                    }}
                                    inputRef={field.ref}
                                    name="PolicyStatusActive"
                                  >
                                    Active
                                  </Checkbox>
                                )}
                              />
                              <Controller
                                control={control}
                                name="PolicyStatusExpired"
                                render={({ field }) => (
                                  <Checkbox
                                    checked={field.value}
                                    onChange={(e) => {
                                      const currentValues = getValues();
                                      const isChecked = e.target.checked;
                                      const isAllPoliciesUnchecked =
                                        !isChecked &&
                                        !currentValues.PolicyStatusLost &&
                                        !currentValues.PolicyStatusCancelled &&
                                        !currentValues.PolicyStatusActive &&
                                        !currentValues.PolicyStatusRenewed;

                                      if (isAllPoliciesUnchecked) {
                                        setValue("PolicyStatusActive", true, {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        });
                                      }
                                      setValue(
                                        "PolicyStatusExpired",
                                        isChecked,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        }
                                      );

                                      debouncedRefreshData();
                                    }}
                                    inputRef={field.ref}
                                    name="PolicyStatusExpired"
                                  >
                                    Expired
                                  </Checkbox>
                                )}
                              />

                              <Controller
                                control={control}
                                name="PolicyStatusRenewed"
                                render={({ field }) => (
                                  <Checkbox
                                    checked={field.value}
                                    onChange={(e) => {
                                      const currentValues = getValues();
                                      const isChecked = e.target.checked;
                                      const isAllPoliciesUnchecked =
                                        !isChecked &&
                                        !currentValues.PolicyStatusLost &&
                                        !currentValues.PolicyStatusCancelled &&
                                        !currentValues.PolicyStatusActive &&
                                        !currentValues.PolicyStatusExpired;

                                      if (isAllPoliciesUnchecked) {
                                        setValue("PolicyStatusActive", true, {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        });
                                      }
                                      setValue(
                                        "PolicyStatusRenewed",
                                        isChecked,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        }
                                      );

                                      debouncedRefreshData();
                                    }}
                                    inputRef={field.ref}
                                    name="PolicyStatusRenewed"
                                  >
                                    Renewed
                                  </Checkbox>
                                )}
                              />
                              <Controller
                                control={control}
                                name="PolicyStatusCancelled"
                                render={({ field }) => (
                                  <Checkbox
                                    checked={field.value}
                                    onChange={(e) => {
                                      const currentValues = getValues();
                                      const isChecked = e.target.checked;
                                      const isAllPoliciesUnchecked =
                                        !isChecked &&
                                        !currentValues.PolicyStatusLost &&
                                        !currentValues.PolicyStatusExpired &&
                                        !currentValues.PolicyStatusActive &&
                                        !currentValues.PolicyStatusRenewed;

                                      if (isAllPoliciesUnchecked) {
                                        setValue("PolicyStatusActive", true, {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        });
                                      }
                                      setValue(
                                        "PolicyStatusCancelled",
                                        isChecked,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        }
                                      );

                                      debouncedRefreshData();
                                    }}
                                    inputRef={field.ref}
                                    name="PolicyStatusCancelled"
                                  >
                                    Cancelled
                                  </Checkbox>
                                )}
                              />

                              <Controller
                                control={control}
                                name="PolicyStatusLost"
                                render={({ field }) => (
                                  <Checkbox
                                    checked={field.value}
                                    onChange={(e) => {
                                      const currentValues = getValues();
                                      const isChecked = e.target.checked;
                                      const isAllPoliciesUnchecked =
                                        !isChecked &&
                                        !currentValues.PolicyStatusRenewed &&
                                        !currentValues.PolicyStatusCancelled &&
                                        !currentValues.PolicyStatusActive &&
                                        !currentValues.PolicyStatusExpired;

                                      if (isAllPoliciesUnchecked) {
                                        setValue("PolicyStatusActive", true, {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        });
                                      }
                                      setValue("PolicyStatusLost", isChecked, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                      });

                                      debouncedRefreshData();
                                    }}
                                    inputRef={field.ref}
                                    name="PolicyStatusLost"
                                  >
                                    Lost
                                  </Checkbox>
                                )}
                              />
                            </Space>
                          </Space>
                        </Form.Item>
                      </Col>
                      <Col {...colSize}>
                        <DynamicProducerSelect
                          formLayout={secondLayout}
                          formLabel="Producer 1"
                          name="ProducerCode1Keys"
                          setValue={setValue}
                          control={control}
                          producerCodesList={producerCodes1List}
                          isProducerCodesListLoading={producerCodes1Loading}
                          afterChange={() => {
                            debouncedRefreshData();
                          }}
                        />
                      </Col>
                      <Col {...colSize}>
                        <DynamicProducerSelect
                          formLayout={secondLayout}
                          formLabel="Producer 2"
                          name="ProducerCode2Keys"
                          setValue={setValue}
                          control={control}
                          producerCodesList={producerCodes2List}
                          isProducerCodesListLoading={producerCodes2Loading}
                          afterChange={() => {
                            debouncedRefreshData();
                          }}
                        />
                      </Col>
                    </Row>
                  </Form>
                ),
              },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div>
            {/* ... existing form code ... */}
            <Row style={{ paddingTop: "16px" }}>
              <Col>
                <Tabs
                  size="small"
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  type="card"
                  items={[
                    { label: "Waiting For Response", key: "1" },
                    { label: "Processing", key: "2" },
                    { label: "Waiting On Customer", key: "3" },
                    { label: "Reminders Sent", key: "4" },
                    { label: "Not Contacted", key: "5" },
                    { label: "Opted Out From Reminders", key: "6" },
                    { label: "Customer Request Resolved", key: "7" },
                    { label: "All", key: "8" },
                  ]}
                />
                <SearchableTable
                  tableName={tableNames.ManageReminders}
                  scroll={{ y: 550, x: 2300 }}
                  dataSource={tabFilteredData}
                  columns={columns}
                  loading={loading}
                  size="small"
                  buttons={[
                    <Button
                      size={"small"}
                      disabled={!isValid}
                      onClick={() => {
                        updateProducerCodes.current = true;
                        debouncedRefreshData.cancel();
                        refreshData();
                      }}
                    >
                      <ReloadOutlined />
                      Refresh Table
                    </Button>,
                    <BulkUpdateActionMenu
                      disabled={selectedRows.length === 0}
                      count={selectedRows.length}
                      onSelect={(actionType) => {
                        setBulkActionType(actionType);
                        setViewDetails(true);
                      }}
                    />
                  ]}
                  rowSelection={rowSelection}
                  rowKey={rowKeyExtractor}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={"No data"}
                      />
                    ),
                  }}
                />
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      {bulkActionType && (
        <BulkUpdateModal
          open={viewDetails}
          onCancel={viewDetailsOnCancelHandler}
          selectedRowRecords={selectedRowRecords}
          filteredSelectedRowsCount={filteredSelectedRowsCount}
          actionType={bulkActionType}
        />
      )}
    </div>
  );
};
export default ManageRemindersForm;
